import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getServices, createService, updateService, deleteService } from '../lib/api';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  ActionButton,
  ActionGroup,
  AppDialog,
  EmptyState,
  LoadingState,
  PageToolbar,
} from '../components/app';
import { ServiceForm } from '../components/services';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  Package,
  TrendUp,
  Cube,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const CATEGORIES = [
  'Desarrollo',
  'Consultoría',
  'Soporte',
  'Infraestructura',
  'Seguridad',
  'Capacitación',
  'Otro',
];

const UNITS = [
  { value: 'hora', label: 'Hora' },
  { value: 'unidad', label: 'Unidad' },
  { value: 'mes', label: 'Mes' },
  { value: 'proyecto', label: 'Proyecto' },
  { value: 'día', label: 'Día' },
];

export function ServicesPage() {
  const { t, isSpanish } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    unit: 'unidad',
    cost_price: '',
    sale_price: '',
    is_active: true,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchServices = useCallback(async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      
      const response = await getServices(params);
      setServices(response.data);
    } catch (error) {
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, searchTerm]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    const costPrice = parseFloat(formData.cost_price);
    const salePrice = parseFloat(formData.sale_price);
    
    if (isNaN(costPrice) || costPrice < 0) {
      toast.error('El costo debe ser un número válido');
      return;
    }
    
    if (isNaN(salePrice) || salePrice < 0) {
      toast.error('El precio de venta debe ser un número válido');
      return;
    }
    
    if (salePrice < costPrice) {
      const confirm = window.confirm('⚠️ El precio de venta es menor que el costo. ¿Deseas continuar?');
      if (!confirm) return;
    }

    try {
      const payload = {
        ...formData,
        cost_price: costPrice,
        sale_price: salePrice,
      };

      if (editingService) {
        await updateService(editingService.service_id, payload);
        toast.success('Servicio actualizado');
      } else {
        await createService(payload);
        toast.success('Servicio creado');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar servicio');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name || '',
      description: service.description || '',
      category: service.category || '',
      unit: service.unit || 'unidad',
      cost_price: service.cost_price || '',
      sale_price: service.sale_price || '',
      is_active: service.is_active !== false,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (serviceId) => {
    if (window.confirm('¿Desactivar este servicio?')) {
      try {
        await deleteService(serviceId);
        toast.success('Servicio desactivado');
        fetchServices();
      } catch (error) {
        toast.error('Error al desactivar servicio');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      unit: 'unidad',
      cost_price: '',
      sale_price: '',
      is_active: true,
    });
    setEditingService(null);
  };

  const calculateMargin = () => {
    const cost = parseFloat(formData.cost_price) || 0;
    const sale = parseFloat(formData.sale_price) || 0;
    if (cost === 0) return 0;
    return (((sale - cost) / cost) * 100).toFixed(1);
  };

  const filteredServices = services.filter((service) =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout title={isSpanish ? 'Catálogo de Servicios' : 'Service Catalog'}>
      <div className="space-y-6">
        {/* Header */}
        <PageToolbar
          filters={
            <>
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                placeholder={isSpanish ? 'Buscar servicios...' : 'Search services...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#151B26] border-[#242E42] text-[#F3F4F6]"
              />
            </div>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48 bg-[#151B26] border-[#242E42]">
                <SelectValue placeholder={isSpanish ? 'Todas las categorías' : 'All categories'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            </>
          }
          actions={
            <AppDialog
              open={isDialogOpen}
              onOpenChange={setIsDialogOpen}
              size="lg"
              title={
                editingService
                  ? isSpanish
                    ? 'Editar Servicio'
                    : 'Edit Service'
                  : isSpanish
                    ? 'Nuevo Servicio'
                    : 'New Service'
              }
              titleClassName="text-[#E2B05E]"
              trigger={
                <ActionButton
                  icon={Plus}
                  label={isSpanish ? 'Nuevo Servicio' : 'New Service'}
                  tone="primary"
                  data-testid="new-service-btn"
                  className="font-semibold"
                  onClick={() => {
                    resetForm();
                    setIsDialogOpen(true);
                  }}
                />
              }
            >
              <ServiceForm
                formData={formData}
                setFormData={setFormData}
                categories={CATEGORIES}
                units={UNITS}
                calculateMargin={calculateMargin}
                editingService={editingService}
                onSubmit={handleSubmit}
                onCancel={() => setIsDialogOpen(false)}
              />
            </AppDialog>
          }
        />
        {/* Services Grid */}
        {loading ? (
          <LoadingState label={isSpanish ? 'Cargando...' : 'Loading...'} />
        ) : filteredServices.length === 0 ? (
          <EmptyState icon={Package} description={isSpanish ? 'No hay servicios' : 'No services found'} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredServices.map((service) => {
              const margin = service.profit_margin || 0;
              const isNegativeMargin = margin < 0;

              return (
                <div
                  key={service.service_id}
                  className="valhalla-card p-5 hover:border-[#E2B05E] transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Cube className="w-5 h-5 text-[#E2B05E]" />
                      <h3 className="font-semibold text-[#F3F4F6]">{service.name}</h3>
                    </div>
                    <Badge
                      className={
                        service.is_active !== false
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }
                    >
                      {service.is_active !== false ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>

                  {service.description && (
                    <p className="text-sm text-[#9CA3AF] mb-3 line-clamp-2">{service.description}</p>
                  )}

                  <div className="space-y-2 mb-4">
                    {service.category && (
                      <div className="flex items-center gap-2 text-xs text-[#9CA3AF]">
                        <span className="px-2 py-1 bg-[#151B26] rounded">
                          {service.category}
                        </span>
                        <span className="px-2 py-1 bg-[#151B26] rounded">
                          {service.unit}
                        </span>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-[#9CA3AF]">Costo:</span>
                        <p className="font-semibold text-[#F3F4F6]">${service.cost_price?.toFixed(2)}</p>
                      </div>
                      <div>
                        <span className="text-[#9CA3AF]">Precio:</span>
                        <p className="font-semibold text-[#E2B05E]">${service.sale_price?.toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-2 bg-[#0B0E14] rounded">
                      <span className="text-xs text-[#9CA3AF]">Margen:</span>
                      <div className="flex items-center gap-1">
                        <TrendUp
                          className={`w-4 h-4 ${isNegativeMargin ? 'text-red-400' : 'text-emerald-400'}`}
                        />
                        <span
                          className={`font-bold ${
                            isNegativeMargin ? 'text-red-400' : 'text-emerald-400'
                          }`}
                        >
                          {margin.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <ActionGroup className="border-t border-[#242E42] pt-3" align="start">
                    <ActionButton
                      icon={PencilSimple}
                      label="Editar"
                      tone="edit"
                      onClick={() => handleEdit(service)}
                      className="flex-1"
                    />
                    <ActionButton
                      icon={Trash}
                      tone="delete"
                      iconOnly
                      onClick={() => handleDelete(service.service_id)}
                    />
                  </ActionGroup>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ServicesPage;
